import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

export interface CountdownTimerState {
  // Timer settings
  hours: number;
  minutes: number;
  seconds: number;
  
  // Timer state
  isRunning: boolean;
  isPaused: boolean;
  timeRemaining: number; // in seconds
  originalTime: number; // in seconds
  
  // Timer controls
  showNotification: boolean;
  playSound: boolean;
  
  // UI state
  error: string | null;
}

const initialState: CountdownTimerState = {
  hours: 0,
  minutes: 0,
  seconds: 0,
  
  isRunning: false,
  isPaused: false,
  timeRemaining: 0,
  originalTime: 0,
  
  showNotification: true,
  playSound: true,
  
  error: null,
};

export const CountdownTimerSlice = createSlice({
  name: "countdownTimer",
  initialState,
  reducers: {
    hoursChanged: (state, action: PayloadAction<number>) => {
      state.hours = Math.max(0, Math.min(99, action.payload));
      state.error = null;
    },
    
    minutesChanged: (state, action: PayloadAction<number>) => {
      state.minutes = Math.max(0, Math.min(59, action.payload));
      state.error = null;
    },
    
    secondsChanged: (state, action: PayloadAction<number>) => {
      state.seconds = Math.max(0, Math.min(59, action.payload));
      state.error = null;
    },
    
    startTimer: (state) => {
      const totalSeconds = state.hours * 3600 + state.minutes * 60 + state.seconds;
      
      if (totalSeconds === 0) {
        state.error = "Please set a time greater than 0";
        return;
      }
      
      state.isRunning = true;
      state.isPaused = false;
      state.timeRemaining = totalSeconds;
      state.originalTime = totalSeconds;
      state.error = null;
    },
    
    pauseTimer: (state) => {
      state.isPaused = true;
      state.isRunning = false;
    },
    
    resumeTimer: (state) => {
      if (state.timeRemaining > 0) {
        state.isRunning = true;
        state.isPaused = false;
      }
    },
    
    stopTimer: (state) => {
      state.isRunning = false;
      state.isPaused = false;
      state.timeRemaining = 0;
      state.originalTime = 0;
    },
    
    resetToOriginal: (state) => {
      state.isRunning = false;
      state.isPaused = false;
      state.timeRemaining = state.originalTime;
    },
    
    tickTimer: (state) => {
      if (state.isRunning && state.timeRemaining > 0) {
        state.timeRemaining -= 1;
        
        if (state.timeRemaining === 0) {
          state.isRunning = false;
          state.isPaused = false;
          
          // Show notification if enabled
          if (state.showNotification) {
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('Countdown Timer', {
                body: 'Time is up!',
                icon: '/favicon.ico',
              });
            }
          }
          
          // Play sound if enabled
          if (state.playSound) {
            try {
              // Use Web Audio API to generate a simple beep sound
              const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
              const oscillator = audioContext.createOscillator();
              const gainNode = audioContext.createGain();
              
              oscillator.connect(gainNode);
              gainNode.connect(audioContext.destination);
              
              oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // 800Hz beep
              oscillator.type = 'sine';
              
              gainNode.gain.setValueAtTime(0.3, audioContext.currentTime); // Volume
              gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
              
              oscillator.start(audioContext.currentTime);
              oscillator.stop(audioContext.currentTime + 0.8);
              
              // Clean up after sound plays
              setTimeout(() => {
                audioContext.close();
              }, 1000);
            } catch (error) {
              console.warn('Could not play notification sound:', error);
            }
          }
        }
      }
    },
    
    setQuickTime: (state, action: PayloadAction<{ hours: number; minutes: number; seconds: number }>) => {
      state.hours = action.payload.hours;
      state.minutes = action.payload.minutes;
      state.seconds = action.payload.seconds;
      state.error = null;
    },
    
    showNotificationChanged: (state, action: PayloadAction<boolean>) => {
      state.showNotification = action.payload;
    },
    
    playSoundChanged: (state, action: PayloadAction<boolean>) => {
      state.playSound = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    resetTimer: (state) => {
      return { ...initialState };
    },
  },
});

// Utility functions
export function formatTime(seconds: number): { hours: number; minutes: number; seconds: number } {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  return { hours, minutes, seconds: secs };
}

export function formatTimeDisplay(seconds: number): string {
  const { hours, minutes, seconds: secs } = formatTime(seconds);
  
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  } else {
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}

export function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    return Promise.resolve(false);
  }
  
  if (Notification.permission === 'granted') {
    return Promise.resolve(true);
  }
  
  if (Notification.permission === 'denied') {
    return Promise.resolve(false);
  }
  
  return Notification.requestPermission().then(permission => permission === 'granted');
}

export const {
  hoursChanged,
  minutesChanged,
  secondsChanged,
  startTimer,
  pauseTimer,
  resumeTimer,
  stopTimer,
  resetToOriginal,
  tickTimer,
  setQuickTime,
  showNotificationChanged,
  playSoundChanged,
  clearError,
  resetTimer,
} = CountdownTimerSlice.actions;

export const selectState = (state: RootState) => state.countdownTimer;

export default CountdownTimerSlice.reducer;
